import { api, handleApiError } from '../client'
import { buildODataQuery, type ODataQueryParams } from '../odata'
import type {
  CreateTemplateRequest,
  TemplateVersion,
  TemplateVersionComparison,
  AnalyzeTemplateResponse,
  TemplateVariableResponse,
  TemplateVariableType,
  ResolvedVariablesResult,
  VariableSourceType,
} from '../types'

export type { AnalyzeTemplateResponse, TemplateVariableResponse }

export interface TemplateOData {
  id: string
  name: string
  description?: string
  category?: string
  type: 'Word' | 'Excel' | 'PowerPoint' | 'Outlook'
  content: string
  variables: TemplateVariableOData[]
  tags: string[]
  thumbnailUrl?: string
  filePath?: string
  fileSize?: number
  originalFileName?: string
  currentVersionNumber: number
  isActive: boolean
  isPublished?: boolean
  createdBy?: string
  createdAt: string
  updatedAt: string
}

export interface TemplateVariableOData {
  name: string
  displayName: string
  type: TemplateVariableType
  required: boolean
  defaultValue?: string | number | boolean | Date | string[]
  description?: string
  format?: string
  validation?: {
    minLength?: number
    maxLength?: number
    min?: number
    max?: number
    pattern?: string
    options?: string[]
  }
  // Content Hub linking
  sourceType?: VariableSourceType
  contentItemId?: string
  contentItemPath?: string
  contentItemName?: string
  contentItemType?: string
}

export interface TemplatesODataResponse {
  value: TemplateOData[]
  '@odata.count'?: number
}

export const templateService = {
  /**
   * Gets templates with OData query support.
   * Supports filtering, sorting, paging via OData parameters.
   */
  async getTemplates(params?: ODataQueryParams): Promise<TemplatesODataResponse> {
    try {
      const queryString = params ? buildODataQuery(params) : ''
      const response = await api.get<TemplatesODataResponse | TemplateOData[]>(`/v1/templates${queryString}`)

      // Handle both OData response and array response for backwards compatibility
      if (Array.isArray(response.data)) {
        return {
          value: response.data,
          '@odata.count': response.data.length
        }
      }
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  async getTemplate(id: string): Promise<TemplateOData> {
    try {
      const response = await api.get<TemplateOData>(`/v1/templates/${id}`)
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  async createTemplate(data: CreateTemplateRequest): Promise<TemplateOData> {
    try {
      const response = await api.post<TemplateOData>('/v1/templates', data)
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  async updateTemplate(id: string, data: Partial<CreateTemplateRequest>): Promise<TemplateOData> {
    try {
      const response = await api.put<TemplateOData>(`/v1/templates/${id}`, data)
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  async deleteTemplate(id: string): Promise<void> {
    try {
      await api.delete(`/v1/templates/${id}`)
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  async publishTemplate(id: string): Promise<void> {
    try {
      await api.post(`/v1/templates/${id}/publish`)
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  async unpublishTemplate(id: string): Promise<void> {
    try {
      await api.post(`/v1/templates/${id}/unpublish`)
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  async duplicateTemplate(id: string, name: string): Promise<TemplateOData> {
    try {
      const response = await api.post<TemplateOData>(`/v1/templates/${id}/duplicate`, { name })
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  async previewTemplate(id: string, variables: Record<string, string | number | boolean | Date | string[]>): Promise<string> {
    try {
      const response = await api.post<{ preview: string }>(
        `/v1/templates/${id}/preview`,
        { variables }
      )
      return response.data.preview
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  async generateDocument(
    templateId: string,
    variables: Record<string, string | number | boolean | Date | string[]>,
    format?: 'pdf' | 'docx' | 'xlsx' | 'pptx'
  ): Promise<Blob> {
    try {
      const response = await api.post<Blob>(
        `/v1/templates/${templateId}/generate`,
        { variables, format },
        { responseType: 'blob' }
      )
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  async getTemplateVersions(id: string): Promise<TemplateVersion[]> {
    try {
      const response = await api.get<TemplateVersion[]>(`/v1/templates/${id}/versions`)
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  async restoreVersion(templateId: string, versionId: string): Promise<TemplateOData> {
    try {
      const response = await api.post<TemplateOData>(
        `/v1/templates/${templateId}/versions/${versionId}/restore`
      )
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  async compareTemplateVersions(
    templateId: string,
    versionId1: string,
    versionId2: string
  ): Promise<TemplateVersionComparison> {
    try {
      const response = await api.get<TemplateVersionComparison>(
        `/v1/templates/${templateId}/versions/compare`,
        { params: { versionId1, versionId2 } }
      )
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  async getTemplateVersion(templateId: string, versionId: string): Promise<TemplateVersion> {
    try {
      const response = await api.get<TemplateVersion>(
        `/v1/templates/${templateId}/versions/${versionId}`
      )
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  async addTemplateTags(id: string, tags: string[]): Promise<void> {
    try {
      await api.post(`/v1/templates/${id}/tags`, { tags })
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  async removeTemplateTag(id: string, tag: string): Promise<void> {
    try {
      await api.delete(`/v1/templates/${id}/tags/${tag}`)
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  async getTemplateCategories(): Promise<string[]> {
    try {
      const response = await api.get<string[]>('/v1/templates/categories')
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  async importTemplate(file: File): Promise<TemplateOData> {
    try {
      const formData = new FormData()
      formData.append('file', file)

      const response = await api.post<TemplateOData>(
        '/v1/templates/import',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      )

      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  async exportTemplate(id: string): Promise<Blob> {
    try {
      const response = await api.get<Blob>(`/v1/templates/${id}/export`, {
        responseType: 'blob',
      })
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  async uploadTemplate(data: {
    file: File
    name?: string
    description?: string
    category?: string
    tags?: string[]
    variables?: TemplateVariableOData[]
  }): Promise<TemplateOData> {
    try {
      const formData = new FormData()
      formData.append('file', data.file)
      if (data.name) formData.append('name', data.name)
      if (data.description) formData.append('description', data.description)
      if (data.category) formData.append('category', data.category)
      if (data.tags) formData.append('tags', JSON.stringify(data.tags))
      if (data.variables) formData.append('variables', JSON.stringify(data.variables))

      const response = await api.post<TemplateOData>(
        '/v1/templates/upload',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      )

      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  async analyzeTemplate(file: File): Promise<AnalyzeTemplateResponse> {
    try {
      const formData = new FormData()
      formData.append('file', file)

      const response = await api.post<AnalyzeTemplateResponse>(
        '/v1/templates/analyze',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      )

      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  // ============================================
  // Variable Linking
  // ============================================

  /**
   * Links a template variable to a Content Hub item.
   * The variable value will be automatically populated from the linked item.
   */
  async linkVariableToContent(
    templateId: string,
    variableName: string,
    contentItemId: string
  ): Promise<void> {
    try {
      await api.post(`/v1/templates/${templateId}/variables/${variableName}/link`, {
        contentItemId,
      })
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Unlinks a template variable from its Content Hub item.
   * The variable will revert to manual input mode.
   */
  async unlinkVariableFromContent(templateId: string, variableName: string): Promise<void> {
    try {
      await api.delete(`/v1/templates/${templateId}/variables/${variableName}/link`)
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Resolves all template variables with their current values.
   * For variables linked to Content Hub items, fetches the latest values.
   */
  async resolveVariables(templateId: string): Promise<ResolvedVariablesResult> {
    try {
      const response = await api.get<ResolvedVariablesResult>(
        `/v1/templates/${templateId}/variables/resolve`
      )
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },
}
